import React from 'react';
import {
  Server,
  RefreshCw,
  Send,
  Play,
  Search,
  X,
} from 'lucide-react';
import type { SapSyncFilterState } from '../../../types/sapSync';

interface SapActionToolbarProps {
  filters: SapSyncFilterState;
  onFilterChange: (filters: Partial<SapSyncFilterState>) => void;
  onResetFilters: () => void;
  onOpenTestSftp: () => void;
  onTriggerInbound: () => void;
  onTriggerDailyOrders: () => void;
  onOpenTriggerJob: () => void;
  loadingInbound: boolean;
  loadingOrders: boolean;
}

export const SapActionToolbar: React.FC<SapActionToolbarProps> = ({
  filters,
  onFilterChange,
  onResetFilters,
  onOpenTestSftp,
  onTriggerInbound,
  onTriggerDailyOrders,
  onOpenTriggerJob,
  loadingInbound,
  loadingOrders,
}) => {
  const hasActiveFilters = Boolean(
    filters.search ||
    filters.status ||
    filters.jobType ||
    filters.direction ||
    filters.startDate ||
    filters.endDate
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs mb-6 overflow-hidden">
      {/* Top Action Buttons Bar */}
      <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Quick Actions & SFTP Triggers
          </h3>
          <p className="text-[11px] text-slate-400">
            Dispatch manual sync runs or test SFTP connection
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* 1. Test SFTP */}
          <button
            onClick={onOpenTestSftp}
            className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all"
          >
            <Server className="w-3.5 h-3.5 text-blue-600" />
            Test SFTP Connection
          </button>

          {/* 2. Scan Inbound SFTP */}
          <button
            onClick={onTriggerInbound}
            disabled={loadingInbound}
            className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingInbound ? 'animate-spin' : ''}`} />
            {loadingInbound ? 'Scanning SFTP...' : 'Scan Inbound SFTP'}
          </button>

          {/* 3. Sync Daily Orders */}
          <button
            onClick={onTriggerDailyOrders}
            disabled={loadingOrders}
            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all"
          >
            <Send className={`w-3.5 h-3.5 ${loadingOrders ? 'animate-pulse' : ''}`} />
            {loadingOrders ? 'Syncing Orders...' : 'Sync Daily Orders to SAP'}
          </button>

          {/* 4. Trigger Custom Job */}
          <button
            onClick={onOpenTriggerJob}
            className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
            Trigger Job
          </button>
        </div>
      </div>

      {/* Filters and Search Row */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Search */}
        <div className="lg:col-span-2 relative">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
            Search
          </label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Run ID, File Name, Job Type..."
              value={filters.search}
              onChange={(e) => onFilterChange({ search: e.target.value })}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => onFilterChange({ status: e.target.value })}
            className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-700"
          >
            <option value="">All Statuses</option>
            <option value="SUCCESS">Success</option>
            <option value="PARTIAL">Partial Failure</option>
            <option value="FAILED">Failed</option>
            <option value="RUNNING">Running / Pending</option>
          </select>
        </div>

        {/* Direction Filter */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
            Direction
          </label>
          <select
            value={filters.direction}
            onChange={(e) => onFilterChange({ direction: e.target.value })}
            className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-700"
          >
            <option value="">All Directions</option>
            <option value="INBOUND">Inbound (SAP → ERP)</option>
            <option value="OUTBOUND">Outbound (ERP → SAP)</option>
          </select>
        </div>

        {/* Job Type Filter */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
            Job Type
          </label>
          <input
            type="text"
            placeholder="e.g. INBOUND_INVOICES"
            value={filters.jobType}
            onChange={(e) => onFilterChange({ jobType: e.target.value })}
            className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-700 placeholder:text-slate-400 font-mono"
          />
        </div>

        {/* Reset Filter Button */}
        <div className="flex items-end">
          {hasActiveFilters && (
            <button
              onClick={onResetFilters}
              className="w-full px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Reset Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SapActionToolbar;
